import { FileRejection, useDropzone } from "react-dropzone";
import classNames from "classnames";
import { useCallback, useContext, useEffect, useMemo } from "react";
import {
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB,
} from "@common/constants/financial-documents";
import { useScope } from "@state/scopeContext.lib";
import {
    formatExtensionsForDropzone,
    isImporterReconciliationQueue,
    isImporterTransactionsQueue,
} from "@common/helpers/financialDocument";
import { TransactionsImportStatus } from "@common/types/financialDocument";
import { FetchFinancialDocumentsResponse } from "@common/dto/financialDocument/fetch-financial-documents-response.dto";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import { useQueryClient } from "@tanstack/react-query";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Separator } from "@design-system/Separator/Separator";
import { useFinancialDocumentsExtendedQuery } from "@components/documents/useFinancialDocumentsExtendedQuery";
import { transactionsImporterDetailIncludes } from "@components/documents/financialDocumentDetailIncludes";
import { DocumentName } from "../documents/DocumentName";
import { ImporterPageContent } from "./ImporterPageContent";
import { ImporterContext } from "./importer.context";
import { useImporterUploadMutation } from "./useImporterUpload";
import styles from "./ImporterPage.module.scss";
import { ImporterPageFooter } from "./ImporterPageFooter";
import { ImporterDocumentPreview } from "./ImporterDocumentPreview";
import { ImporterPreviewNavigation } from "./ImporterPreviewNavigation";
import {
    documentBeingProcessed,
    isImportRunning,
    isRowReadyForImport,
} from "./helpers";
import { ImporterPreviewActions } from "./ImporterPreviewActions";
import { ImporterPageStatus } from "./ImporterPageStatus";
import { IMPORTER_TOAST_KEY } from "./ImporterToast.lib";
import { ImporterToast } from "./ImporterToast";
import {
    IMPORTER_FILE_TYPES,
    RECONCILIATION_FILE_TYPES,
    RECONCILIATION_FILE_TYPES_NAMES,
    IMPORTER_FILE_TYPES_NAMES,
} from "./constants";

const sizeSuffix = `of size less than ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB} MB are allowed.`;
const errorMessageReconciliation = `Only ${RECONCILIATION_FILE_TYPES_NAMES} ${sizeSuffix}`;
const errorMessageImporter = `Only ${IMPORTER_FILE_TYPES_NAMES} ${sizeSuffix}`;

const REFETCH_INTERVAL_MS = 7_000;

type ImporterLayout = "page" | "drawer";

interface Props {
    onHide: () => void;
    onNext?: () => Promise<void>;
    layout?: ImporterLayout;
}

const CONTAINER_CLASS_BY_LAYOUT: Record<ImporterLayout, string> = {
    page: styles.container,
    drawer: classNames(styles.container, styles.drawerContainer),
};

export const Importer: React.FC<Props> = ({
    onHide,
    onNext,
    layout = "page",
}) => {
    const {
        upsertDocumentRow,
        previewedRowId,
        setPreviewedRowId,
        documentRows,
        sortedRows,
        importerQueue,
        reconciliedFinancialAccount,
        removeDocumentRow,
        refreshEnabled,
    } = useContext(ImporterContext);
    const toaster = useToaster();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const queryClient = useQueryClient();
    const upload = useImporterUploadMutation(() => {
        toaster.toast(<ImporterToast />, IMPORTER_TOAST_KEY);
    });
    const { workspaceId: activeWorkspaceId } = useScope();
    const rowsReadyForImport = useMemo(
        () =>
            documentRows.filter(
                (row) =>
                    isRowReadyForImport(
                        row,
                        importerQueue,
                        reconciliedFinancialAccount,
                    ) &&
                    (!row.financialDocument?.transactionsImportStatus ||
                        row.financialDocument.transactionsImportStatus ===
                            TransactionsImportStatus.FAILED ||
                        !isImporterTransactionsQueue(importerQueue)),
            ),
        [documentRows, importerQueue, reconciliedFinancialAccount],
    );
    const allDocumentsImported = useMemo(
        () =>
            documentRows.length > 0 &&
            documentRows.every(
                (row) =>
                    row.financialDocument?.transactionsImportStatus ===
                    TransactionsImportStatus.FINISHED,
            ),
        [documentRows],
    );
    const handleDocumentsRemovedFromImporter = useCallback(
        (importerDocuments: FetchFinancialDocumentsResponse) => {
            const returnedDocumentsIds = importerDocuments.data.map(
                (document) => document.id,
            );
            const documentRowsToClear = documentRows.filter(
                (row) =>
                    row.financialDocument &&
                    !returnedDocumentsIds.includes(row.financialDocument.id),
            );
            let accountsRefreshRequired = false;
            documentRowsToClear.forEach((row) => {
                if (row.financialDocument?.transactionsImportStatus) {
                    upsertDocumentRow({
                        ...row,
                        financialDocument: {
                            ...row.financialDocument,
                            transactionsImportStatus:
                                TransactionsImportStatus.FINISHED,
                        },
                    });
                    if (
                        row.financialDocument.transactionsImportStatus !==
                        TransactionsImportStatus.FINISHED
                    ) {
                        accountsRefreshRequired = true;
                    }
                } else {
                    removeDocumentRow(row);
                }
            });

            if (accountsRefreshRequired) {
                queryClient.invalidateQueries({
                    queryKey: getEntitiesAccountsQueryKey(
                        activeWorkspaceId ?? "",
                    ),
                });
            }
        },
        [
            upsertDocumentRow,
            removeDocumentRow,
            documentRows,
            activeWorkspaceId,
            queryClient,
        ],
    );

    const { data: importerDocuments, refetch } =
        useFinancialDocumentsExtendedQuery({
            queryParams: {
                workspaceId: activeWorkspaceId ?? "",
                filters: {
                    importerQueue,
                },
                limit: 0,
                page: 0,
            },
            detailIncludes: transactionsImporterDetailIncludes,
            onSuccess: handleDocumentsRemovedFromImporter,
        });

    useEffect(() => {
        importerDocuments?.data.forEach((document) => {
            upsertDocumentRow({
                id: document.id.toString(),
                financialDocument: document,
                integrationAccount: document.integrationAccount ?? undefined,
            });
        });
    }, [importerDocuments, upsertDocumentRow]);

    const requiresRefetch = useMemo(
        () =>
            documentRows.length > 0 &&
            documentRows.some(
                (row) =>
                    row.financialDocument &&
                    (documentBeingProcessed(row.financialDocument) ||
                        isImportRunning(row.financialDocument)),
            ),
        [documentRows],
    );

    useEffect(() => {
        if (requiresRefetch && refreshEnabled) {
            const interval = setInterval(() => {
                refetch();
            }, REFETCH_INTERVAL_MS);
            return () => clearInterval(interval);
        }
    }, [refetch, requiresRefetch, refreshEnabled]);

    const isReconciliationQueue = isImporterReconciliationQueue(importerQueue);
    const errorMessage = useMemo(() => {
        if (isReconciliationQueue) {
            return errorMessageReconciliation;
        }
        return errorMessageImporter;
    }, [isReconciliationQueue]);

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            // The drag-and-drop path, and the last line of defence for every
            // route that reaches this modal. Without it a view-only user gets a
            // row stuck at "Uploading" until the server 403s (PRO-11392).
            if (refuseIfViewOnly()) {
                return;
            }

            const timestamp = Date.now();

            for (const [index, file] of acceptedFiles.entries()) {
                const id = `${timestamp}-${index}`;
                upsertDocumentRow({
                    id,
                    file,
                });
                upload.mutate({
                    file,
                    documentId: id,
                });
            }

            if (fileRejections.length === 1) {
                toaster.toast(errorMessage);
            }

            if (fileRejections.length > 1) {
                toaster.toast(
                    `${fileRejections.length} files rejected. ${errorMessage}`,
                );
            }
        },
        [upsertDocumentRow, toaster, upload, errorMessage, refuseIfViewOnly],
    );

    const acceptedFileTypes = useMemo(() => {
        if (isReconciliationQueue) {
            return formatExtensionsForDropzone(RECONCILIATION_FILE_TYPES);
        }
        return formatExtensionsForDropzone(IMPORTER_FILE_TYPES);
    }, [isReconciliationQueue]);
    const {
        getRootProps,
        getInputProps,
        open: openUploadPrompt,
    } = useDropzone({
        onDrop,
        accept: acceptedFileTypes,
        maxSize: MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
        noClick: true,
        noKeyboard: true,
    });

    const previewedIndex = useMemo(
        () =>
            previewedRowId
                ? sortedRows.findIndex((row) => row.id === previewedRowId)
                : -1,
        [sortedRows, previewedRowId],
    );

    const previewSibling = useCallback(
        (direction: "next" | "previous") => {
            if (previewedIndex === -1) {
                return;
            }

            const newIndex =
                direction === "next" ? previewedIndex + 1 : previewedIndex - 1;

            if (newIndex < 0 || newIndex >= sortedRows.length) {
                return;
            }

            setPreviewedRowId(sortedRows[newIndex].id);
        },
        [sortedRows, previewedIndex, setPreviewedRowId],
    );

    const previewNext = useCallback(() => {
        previewSibling("next");
    }, [previewSibling]);

    const previewPrevious = useCallback(() => {
        previewSibling("previous");
    }, [previewSibling]);

    const previewedRow = useMemo(
        () => documentRows.find((row) => row.id === previewedRowId),
        [documentRows, previewedRowId],
    );

    useKeyboardCommand({
        keys: "Escape",
        callback: () => setPreviewedRowId(undefined),
        options: {
            enabled: !!previewedRow,
            preventDefault: true,
            description: "Close document preview",
        },
    });

    return (
        <>
            <div
                className={CONTAINER_CLASS_BY_LAYOUT[layout]}
                {...getRootProps()}
            >
                <ImporterPageStatus />
                <ImporterPageContent />
                <input {...getInputProps()} />
            </div>
            <ImporterPageFooter
                rowsReadyForImport={rowsReadyForImport}
                allDocumentsImported={allDocumentsImported}
                onHide={onHide}
                onNext={onNext}
                openUploadPrompt={openUploadPrompt}
            />

            <Drawer
                open={!!previewedRow}
                direction="right"
                modal={false}
                size="fullScreen"
                dismissible={false}
                headerClassName={styles.drawerHeader}
                headerAddonLeft={
                    previewedRow?.financialDocument && (
                        <DocumentName
                            fileName={previewedRow.financialDocument.fileName}
                            icon
                            documentId={previewedRow.financialDocument.id}
                            textClassName={styles.drawerDocumentName}
                        />
                    )
                }
                headerAddonBeforeClose={
                    previewedRow?.financialDocument && (
                        <div className={styles.headerRight}>
                            <ImporterPreviewActions
                                financialDocument={
                                    previewedRow.financialDocument
                                }
                                previewedRow={previewedRow}
                                onRemoved={() => setPreviewedRowId(undefined)}
                            />
                            <ImporterPreviewNavigation
                                onNext={previewNext}
                                onPrevious={previewPrevious}
                                currentIndex={previewedIndex + 1}
                                totalCount={sortedRows.length}
                            />
                            <Separator
                                orientation="vertical"
                                className={styles.headerSeparator}
                            />
                        </div>
                    )
                }
                onClose={() => setPreviewedRowId(undefined)}
                noPadding
            >
                {previewedRow?.financialDocument && (
                    <div className={styles.drawerContent}>
                        <ImporterDocumentPreview
                            financialDocument={previewedRow.financialDocument}
                        />
                    </div>
                )}
            </Drawer>
        </>
    );
};
